import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { apiClient } from '../services/api';
import { ROUTES } from '../config/constants';
import toast from 'react-hot-toast';
import { clearUserContext, addBreadcrumb } from '../lib/sentry';

export const useDeleteAccount = () => {
  const [isDeleting, setIsDeleting] = useState(false);
  const { user, logout: storeLogout } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const deleteAccount = async (): Promise<{ success: boolean; error?: string }> => {
    if (!user) {
      return { success: false, error: 'No user logged in' };
    }

    try {
      setIsDeleting(true);
      
      // Add breadcrumb for tracking
      addBreadcrumb(
        'User initiated account deletion',
        'auth'
      );

      // Call backend to delete all user data
      // The backend will cascade delete all projects, episodes, segments, vocabulary, etc.
      // and also handle Supabase auth user deletion
      try {
        const response = await apiClient.post('/delete/user', {
          user_id: user.user_metadata?.username || user.email?.split('@')[0] || user.id,
        });
        
        if (response.data.status !== 'success') {
          throw new Error(response.data.message || 'Failed to delete account');
        }
      } catch (backendError: any) {
        const errorMessage = backendError.response?.data?.message || backendError.message || 'Failed to delete account data';
        throw new Error(errorMessage);
      }

      // Clear all cached data
      queryClient.clear();
      
      // Clear user context from Sentry
      clearUserContext();
      
      // Clear auth store
      storeLogout();

      // Show success message
      toast.success('Your account has been successfully deleted');
      
      // Add breadcrumb for successful deletion
      addBreadcrumb(
        'Account successfully deleted',
        'auth'
      );

      // Navigate to login page
      navigate(ROUTES.LOGIN);

      return { success: true };
    } catch (error) {
      console.error('Error deleting account:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete account';
      
      // Add breadcrumb for error
      addBreadcrumb(
        `Account deletion failed: ${errorMessage}`,
        'auth'
      );
      
      toast.error(errorMessage);
      
      return { success: false, error: errorMessage };
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    deleteAccount,
    isDeleting,
  };
};
